import { ReactNode, useState, useEffect, useCallback } from 'react';
import { ThemeProvider } from 'styled-components';
import {
  AntDesignUIProvider,
  ThemeConfig,
  Spin,
} from '@goalsetter/ant-design-ui';
import { useConfigApi } from '../api';
import { LoadingOutlined } from '@ant-design/icons';
import type { DefaultTheme } from 'styled-components';
import { useBrandingStyles } from '@/hooks/useBrandingStyles';
import { useBrandingConfig } from '@/hooks';

export default function ThemeProviderWrapper({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  const [theme, setTheme] = useState<ThemeConfig | undefined>(undefined);
  const { getTheme } = useConfigApi();
  const { styles: brandingStyles, isLoading: brandingStylesLoading } =
    useBrandingStyles();
  const { error: brandingStylesError } = useBrandingConfig();

  const initThemes = useCallback(() => {
    try {
      const finalTheme = brandingStyles;
      setTheme(finalTheme as ThemeConfig);
    } catch (error) {
      console.error('Failed to load theme:', error);
    }
  }, [brandingStyles]);

  const initDefaultTheme = useCallback(async () => {
    const defaultTheme: ThemeConfig = await getTheme();
    setTheme(defaultTheme);
  }, [getTheme]);

  useEffect(() => {
    if (!brandingStylesLoading) {
      initThemes();
    }
  }, [brandingStyles, brandingStylesLoading, initThemes]);

  useEffect(() => {
    if (brandingStylesError) {
      initDefaultTheme();
    }
  }, [brandingStylesError, initDefaultTheme]);

  const spinnerIcon = { fontSize: 64, color: 'white' };

  // Only block on the first theme resolution. Later branding/style reloads must
  // keep children mounted, otherwise their effects run again on remount.
  if (!theme) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin
          indicator={
            <LoadingOutlined
              style={spinnerIcon}
              spin
            />
          }
        />
      </div>
    );
  }

  return (
    <AntDesignUIProvider theme={theme}>
      <ThemeProvider theme={theme.token as DefaultTheme}>
        <div style={{ backgroundColor: '#ccc' }}>{children}</div>
      </ThemeProvider>
    </AntDesignUIProvider>
  );
}
